// JWT token management utilities

import { STORAGE_KEYS } from '../../constants/apiConstants';

class TokenManager {
  getCurrentSpace() {
    // The URL decides the space. Admin lives under /admin, everything else is
    // the storefront, so a leftover last_active_space=admin must not make a
    // storefront page read the admin token slot (and send no token at all).
    const path = window.location?.pathname || '';
    if (path.startsWith('/admin') || path.startsWith('/Admin')) {
      return 'admin';
    }

    return 'user';
  }

  getAccessToken(space) {
    // If explicit space provided, use it
    if (space === 'admin') return localStorage.getItem(STORAGE_KEYS.ADMIN_ACCESS_TOKEN);
    if (space === 'user') return localStorage.getItem(STORAGE_KEYS.USER_ACCESS_TOKEN);

    // Otherwise, try to detect space
    const currentSpace = this.getCurrentSpace();
    let token = currentSpace === 'admin'
      ? localStorage.getItem(STORAGE_KEYS.ADMIN_ACCESS_TOKEN)
      : localStorage.getItem(STORAGE_KEYS.USER_ACCESS_TOKEN);

    // Do not fallback to the other space; keep admin and user sessions separate.
    if (!token) {
      token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    }

    return token;
  }

  setAccessToken(token, space) {
    const currentSpace = space || this.getCurrentSpace();
    localStorage.setItem('last_active_space', currentSpace);
    
    if (!token) {
      if (currentSpace === 'admin') {
        localStorage.removeItem(STORAGE_KEYS.ADMIN_ACCESS_TOKEN);
      } else {
        localStorage.removeItem(STORAGE_KEYS.USER_ACCESS_TOKEN);
      }
      return;
    }
    if (currentSpace === 'admin') {
      localStorage.setItem(STORAGE_KEYS.ADMIN_ACCESS_TOKEN, token);
    } else {
      localStorage.setItem(STORAGE_KEYS.USER_ACCESS_TOKEN, token);
    }
  }

  clearTokens(space) {
    // Legacy space-agnostic keys from an older build. getAccessToken() still
    // falls back to ACCESS_TOKEN, so leaving them behind silently restores the
    // previous session on the next refresh.
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.ROLE);

    if (space === 'admin') {
      localStorage.removeItem(STORAGE_KEYS.ADMIN_ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.ADMIN_USER_DATA);
      localStorage.removeItem(STORAGE_KEYS.ADMIN_ROLE);
    } else if (space === 'user') {
      localStorage.removeItem(STORAGE_KEYS.USER_ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_USER_DATA);
      localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
    } else {
      // Clear both if no space specified
      localStorage.removeItem(STORAGE_KEYS.ADMIN_ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.ADMIN_USER_DATA);
      localStorage.removeItem(STORAGE_KEYS.ADMIN_ROLE);
      localStorage.removeItem(STORAGE_KEYS.USER_ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_USER_DATA);
      localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
    }
  }

  isAuthenticated() {
    return !!this.getAccessToken();
  }

  decodeToken(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  isTokenExpired(token) {
    if (!token) return true;
    
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  }

  getUserFromToken() {
    const token = this.getAccessToken();
    if (!token) return null;
    
    return this.decodeToken(token);
  }

  getUserRole() {
    const user = this.getUserFromToken();
    return user?.role || null;
  }

  setUserData(userData, space) {
    const currentSpace = space || this.getCurrentSpace();
    if (userData) {
      if (currentSpace === 'admin') {
        localStorage.setItem(STORAGE_KEYS.ADMIN_USER_DATA, JSON.stringify(userData));
      } else {
        localStorage.setItem(STORAGE_KEYS.USER_USER_DATA, JSON.stringify(userData));
      }
      if (userData.role) {
        const normalizedRole = userData.role.toLowerCase();
        if (currentSpace === 'admin') {
          localStorage.setItem(STORAGE_KEYS.ADMIN_ROLE, normalizedRole);
        } else {
          localStorage.setItem(STORAGE_KEYS.USER_ROLE, normalizedRole);
        }
      }
    }
  }

  getUserData(space) {
    if (space === 'admin') return this._parseUserData(STORAGE_KEYS.ADMIN_USER_DATA);
    if (space === 'user') return this._parseUserData(STORAGE_KEYS.USER_USER_DATA);

    const currentSpace = this.getCurrentSpace();
    return this._parseUserData(
      currentSpace === 'admin' ? STORAGE_KEYS.ADMIN_USER_DATA : STORAGE_KEYS.USER_USER_DATA
    );
  }

  _parseUserData(key) {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      return null;
    }
  }

  getRole(space) {
    const currentSpace = space || this.getCurrentSpace();
    if (currentSpace === 'admin') {
      return localStorage.getItem(STORAGE_KEYS.ADMIN_ROLE);
    } else {
      return localStorage.getItem(STORAGE_KEYS.USER_ROLE);
    }
  }
}

// Export singleton instance
const tokenManager = new TokenManager();
export default tokenManager;
